import { z } from 'zod';

import { IngredientSchema } from './base.js';
import { BaseModelSchema, OptionalTimestampSchema } from './common.js';
import { RecipeNutritionFormSchema, RecipeNutritionSchema } from './nutrition.js';

// ─── Empty-String Sanitizer (Genesis v4.1) ───────────────────────────────────
// React Hook Form submits empty strings for blank URL fields, which causes
// Zod .url() validation crashes. This preprocessor converts "" to undefined
// before validation, preserving the "no value provided" semantic.
const emptyStringToUndefined = (val: unknown) => val === '' ? undefined : val;

// ─── Taxonomy Manifest Schema (Reverse-HyDE) ────────────────────────────────
// Replaces the conversational bgeOptimizedSummary with a structured manifest
// optimized for BGE embedding model attention-head weighting.
export const TaxonomyManifestSchema = z.object({
    structuralClass: z.string(),
    culinaryAttributes: z.array(z.string()),
    syntheticQueries: z.array(z.string()),
});

export type TaxonomyManifest = z.infer<typeof TaxonomyManifestSchema>;

// Recipe Schema for Database
export const RecipeSchema = BaseModelSchema.extend({
    title: z.string().min(1, 'Recipe title is required'),
    description: z.string().optional(),
    instructions: z.string().optional(),
    prepTimeMinutes: z.number().int().optional(),
    cookTimeMinutes: z.number().int().optional(),
    totalTimeMinutes: z.number().int().optional(),
    servings: z.number().int().optional(),
    cookTemperatureC: z.number().optional(),
    temperatureContext: z.string().optional(),
    originalTemperatureUnit: z.enum(['C', 'F']).nullable().optional(),
    difficulty: z.enum(['easy', 'medium', 'hard']).optional(),
    imageUrl: z.preprocess(emptyStringToUndefined, z.string().url().optional().nullable()),
    sourceUrl: z.preprocess(emptyStringToUndefined, z.string().url().optional().nullable()),
    sourceName: z.string().optional().nullable(),
    sourceAuthor: z.string().optional().nullable(),
    ingestionMethod: z.enum(['manual_entry', 'web_scraper', 'document_parser', 'workspace_clone', 'api_integration']).optional(),
    isPublic: z.boolean().optional(),
    userId: z.string().uuid(),
    workspaceId: z.string().uuid().optional(),
    originalRecipeId: z.string().uuid().optional(),
    embedding: z.array(z.number()).optional(),
    ingredients: z.array(IngredientSchema).optional(),
    categories: z.array(z.object({ id: z.string().optional(), source: z.enum(['user', 'ai', 'web']).optional() }).passthrough()).optional(),
    nutrition: RecipeNutritionSchema.optional(),
    aiAnalysis: z.string().optional(),
    taxonomyManifest: TaxonomyManifestSchema.optional().nullable(),
    auditLog: z.string().optional(),
    notes: z.string().optional().nullable(),
    rating: z.number().int().min(1, 'Rating must be at least 1').max(5, 'Rating must be at most 5').optional().nullable(),
    sourceRating: z.number().min(0).optional().nullable(),
    sourceReviewCount: z.number().int().min(0).optional().nullable(),
    hasAiAnalysis: z.boolean().optional(),
    similarity: z.number().nullable().optional(),
    matchType: z.enum(['exact', 'semantic']).optional(),
});

// Recipe Schema for Form Inputs (Strict Base — no .passthrough(), safe for RHF generics)
export const RecipeFormSchemaBase = z.object({
    id: z.string().uuid().optional(),
    title: z.string().min(1, 'Recipe title is required'),
    description: z.string().optional(),
    instructions: z.string().optional(),
    prepTimeMinutes: z.number().int().optional(),
    cookTimeMinutes: z.number().int().optional(),
    totalTimeMinutes: z.number().int().optional(),
    servings: z.number().int().optional(),
    cookTemperatureC: z.number().optional(),
    temperatureContext: z.string().optional(),
    originalTemperatureUnit: z.enum(['C', 'F']).nullable().optional(),
    difficulty: z.enum(['easy', 'medium', 'hard']).optional(),
    imageUrl: z.preprocess(emptyStringToUndefined, z.string().url().optional().nullable()),
    sourceUrl: z.preprocess(emptyStringToUndefined, z.string().url().optional().nullable()),
    sourceName: z.string().optional().nullable(),
    sourceAuthor: z.string().optional().nullable(),
    ingestionMethod: z.enum(['manual_entry', 'web_scraper', 'document_parser', 'workspace_clone', 'api_integration']).optional(),
    isPublic: z.boolean().optional(),
    userId: z.string().uuid().optional(),
    workspaceId: z.string().uuid().optional(),
    originalRecipeId: z.string().uuid().optional(),
    embedding: z.array(z.number()).optional(),
    createdAt: OptionalTimestampSchema,
    updatedAt: OptionalTimestampSchema,
    ingredients: z.array(IngredientSchema).optional(),
    nutrition: RecipeNutritionFormSchema,
    categories: z.array(z.object({ id: z.string().optional(), source: z.enum(['user', 'ai', 'web']).optional() }).passthrough()).optional(),
    aiAnalysis: z.string().optional(),
    taxonomyManifest: TaxonomyManifestSchema.optional().nullable(),
    auditLog: z.string().optional(),
    notes: z.string().optional().nullable(),
    rating: z.number().int().min(1, 'Rating must be at least 1').max(5, 'Rating must be at most 5').optional().nullable(),
    sourceRating: z.number().min(0).optional().nullable(),
    sourceReviewCount: z.number().int().min(0).optional().nullable(),
});

// Portable runtime schema — .passthrough() preserves hrv:* metadata per Rule 33
export const RecipeFormSchema = RecipeFormSchemaBase.passthrough();

// Strict inference for RHF generics — avoids passthrough collapse on UseFieldArrayReturn
export type RecipeFormInput = z.infer<typeof RecipeFormSchemaBase>;

// Recipe Update Schema (partial updates)
export const RecipeUpdateSchema = RecipeFormSchema.partial();

// Recipe Search Schema
export const RecipeSearchSchema = z.object({
    query: z.string().optional(),
    categoryIds: z.array(z.string()).optional(),
    difficulty: z.enum(['easy', 'medium', 'hard']).optional(),
    maxPrepTime: z.number().int().optional(),
    maxCookTime: z.number().int().optional(),
    maxTotalTime: z.number().int().optional(),
    minServings: z.number().int().optional(),
    maxServings: z.number().int().optional(),
    isPublic: z.boolean().optional(),
    userId: z.string().uuid().optional(),
    workspaceId: z.string().uuid().optional(),
    limit: z.number().int().min(1).max(100).default(20),
    offset: z.number().int().min(0).default(0),
    sortBy: z.enum(['createdAt', 'updatedAt', 'title', 'prepTimeMinutes', 'cookTimeMinutes', 'sourceRating', 'sourceReviewCount']).default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

// ─── Import Error Schema (Phase 17: Sheet Context Injection) ───────────────────
// Unified DTO for batch import telemetry — consumed by backend validation/payload
// services and frontend error manifest generator.

export const ImportRowErrorSchema = z.object({
    row: z.number(),
    column: z.string(),
    title: z.string(),
    message: z.string(),
    sheet: z.string(),
});

export type ImportRowError = z.infer<typeof ImportRowErrorSchema>;

export type Recipe = z.infer<typeof RecipeSchema>;
export type RecipeForm = z.infer<typeof RecipeFormSchema>;
export type RecipeUpdate = z.infer<typeof RecipeUpdateSchema>;
export type RecipeSearch = z.infer<typeof RecipeSearchSchema>;